import React, { useCallback, useContext, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useGetBandValueLazyQuery } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  A11yTooltip,
  ActionButtons,
  Calculation,
} from "../../Common/__styles__/BottomBanner";
import { BottomBanner } from "../../Common/BottomBanner";
import { Button } from "../../Common/Button";
import { Loading } from "../../Common/Icons/Loading";
import { IconNames } from "../../Common/Icons/LucideIcons";
import { Body } from "../../Common/Typography";
import { GetHelpLink } from "../../Guest/GetHelpLink";
import { LayerContext, Raster } from "../layers";
import { usedEscapeKey } from "./utils";

export const RasterMeasure = ({
  title,
  subtitle,
  parentLoading = false,
  actionName,
  primaryButtonIconName,
  secondaryButton,
  onActionClick,
  raster,
}: {
  title: React.ReactNode | string;
  subtitle: string;
  parentLoading?: boolean;
  actionName: string;
  primaryButtonIconName?: IconNames;
  secondaryButton?: React.ReactNode;
  onActionClick: (value: string) => void;
  raster: Raster;
}) => {
  const { t } = useTranslation();
  const { addErrorToast } = useStatusToasts();
  const {
    measureToolDispatch,
    measureToolState,
    toggleLayer,
    visibleRaster: getVisibleRaster,
  } = useContext(LayerContext);

  const [getBandValue, { loading: getBandValueLoading, data }] =
    useGetBandValueLazyQuery({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        measureToolDispatch({
          type: "setLoading",
          data: { loading: false },
        });
      },
      onError: () => {
        measureToolDispatch({
          type: "setLoading",
          data: { loading: false },
        });
        addErrorToast(
          "There was an error obtaining a value. Please try again.",
        );
      },
    });

  useEffect(() => {
    if (
      !getBandValueLoading &&
      !parentLoading &&
      measureToolState.measureToolMode === "raster" &&
      measureToolState.coordinates.length
    ) {
      measureToolDispatch({
        type: "setLoading",
        data: { loading: true },
      });
      void getBandValue({
        variables: {
          ...measureToolState.coordinates[0]!,
          rasterId: raster.id,
        },
      });
    }
  }, [measureToolState.coordinates]);

  useEffect(() => {
    if (
      measureToolState.coordinates.length &&
      measureToolState.dataMode === "observe"
    ) {
      measureToolDispatch({
        type: "setLoading",
        data: { loading: true },
      });
      void getBandValue({
        variables: {
          ...measureToolState.coordinates[0]!,
          rasterId: raster.id,
        },
      });
    }
  }, [raster.id]);

  const bandValue = data?.getBandValue;
  const hasMeasurement = !!bandValue;
  let measurementDisplay: React.ReactNode;
  let measurementValue = "";
  if (bandValue === undefined) {
    measurementDisplay = <div data-testid="undefined-display">-</div>;
  } else if (bandValue === null) {
    measurementDisplay = (
      <div style={{ fontStyle: "italic" }}>
        {t("measure-raster-no-data-available")}
      </div>
    );
  } else {
    measurementValue = bandValue.toFixed(3);
    measurementDisplay = (
      <div>
        {measurementValue} {raster.bandMappings.measurementUnit}
      </div>
    );
  }

  const primaryButton = (
    <Button
      styleVariant="outlineDark"
      size="small"
      onClick={() => onActionClick(measurementValue)}
      leftIconName={primaryButtonIconName}
      loading={parentLoading}
      tabIndex={-1}
    >
      {actionName}
    </Button>
  );

  const rasterIsVisible = getVisibleRaster()?.id === raster.id;
  const layerName = rasterIsVisible ? "base" : "raster";

  const onToggleBaseLayer = () => {
    toggleLayer({
      group: "rasters",
      id: raster.id,
      isVisible: !rasterIsVisible,
    });
  };

  const onClose = () => {
    if (parentLoading) {
      return;
    }
    measureToolDispatch({
      type: "setMeasureMode",
      data: { measureToolMode: "off" },
    });

    if (rasterIsVisible) {
      toggleLayer({
        group: "rasters",
        id: raster.id,
        isVisible: false,
      });
    }
  };

  return (
    <MeasureInfo title={title} subtitle={subtitle} onClose={onClose}>
      <Body
        as="div"
        type="emphasis"
        size="default"
        color="contentPrimaryInverted"
      >
        <Calculation>
          {t("measure-raster-label", {
            measurementName: raster.bandMappings.measurementName,
          })}
          {getBandValueLoading ?
            <Loading size="small" data-testid="loading" />
          : measurementDisplay}
        </Calculation>
      </Body>
      <ActionButtons>
        <Button
          styleVariant="outlineDark"
          size="small"
          onClick={onToggleBaseLayer}
          tabIndex={-1}
        >
          {t("measure-raster-show-layer", { layerName })}
        </Button>
        {hasMeasurement && !getBandValueLoading && primaryButton}
        {secondaryButton}
      </ActionButtons>
    </MeasureInfo>
  );
};

export const MeasureInfo = ({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: React.ReactNode;
  subtitle: string;
  children: React.ReactNode;
  onClose: () => void;
}) => {
  const { isPublic } = useContext(AuthContext);
  const { a11yEnabled } = useContext(LayerContext);
  const a11yTooltipRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // casting e to React.KeyboardEvent to avoid type errors
      if (usedEscapeKey({ key: e.key } as React.KeyboardEvent<HTMLElement>)) {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (a11yEnabled && isPublic) {
      a11yTooltipRef.current?.focus();
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [a11yEnabled, isPublic]);

  const tooltipComponent = a11yEnabled && isPublic && (
    <A11yTooltip ref={a11yTooltipRef} tabIndex={0}>
      <Body type="regular" size="default" color="contentPrimaryInverted">
        <Trans
          i18nKey="measure-tooltip-component"
          components={{ getHelpLink: <GetHelpLink /> }}
        />
      </Body>
    </A11yTooltip>
  );

  return (
    <BottomBanner
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      tooltipComponent={tooltipComponent}
    >
      {children}
    </BottomBanner>
  );
};
